import React, { useContext, useEffect, useMemo, useState } from 'react'

import classNames from 'classnames'

import { MemoBox } from 'ui/components/Box/Box'
import { ColorScheme } from 'ui/styling/baseVariables/colors/colorPalette'
import { Breakpoint, breakpoints } from 'ui/styling/helpers/breakpoints'

import { ThemeProviderProps, ThemeProviderRef, themes } from './themeTypes'
import { ThemeContext } from './useTheme'

const ThemeProviderInner = React.forwardRef<ThemeProviderRef, ThemeProviderProps>(
    ({ children, className, theme = 'default', ...props }, ref) => {
        const { className: themeClassName, rawVariables: themeRawVariables } = useMemo(
            () => themes[theme],
            [theme]
        )

        // If we are nested inside another ThemeProvider, use the parent context.
        const parentContext = useContext(ThemeContext)

        const [localCurrentBreakpoint, localSetCurrentBreakpoint] = useState<Breakpoint>('mobile')
        useEffect(() => {
            if (!!parentContext?.breakpoint) return

            const handleResize = () => {
                const width = window.innerWidth
                const breakpoint = Object.entries(breakpoints).find(
                    ([_, { start, end }]) => width >= start && (end === undefined || width < end)
                )
                if (breakpoint) {
                    localSetCurrentBreakpoint(breakpoint[0] as Breakpoint)
                }
            }

            handleResize()
            window.addEventListener('resize', handleResize)
            return () => window.removeEventListener('resize', handleResize)
        }, [parentContext?.breakpoint])

        const currentBreakpoint = parentContext?.breakpoint ?? localCurrentBreakpoint

        const [localColorScheme, localSetColorScheme] = useState<ColorScheme>('Indigo')
        const currentColorScheme = parentContext?.colorScheme ?? localColorScheme
        const setColorScheme = parentContext?.setColorScheme ?? localSetColorScheme

        const value = useMemo(
            () => ({
                theme,
                themeClassName,
                themeRawVariables,
                breakpoint: currentBreakpoint,
                colorScheme: currentColorScheme,
                setColorScheme,
            }),
            [
                theme,
                themeClassName,
                themeRawVariables,
                currentBreakpoint,
                currentColorScheme,
                setColorScheme,
            ]
        )
        return (
            <ThemeContext.Provider value={value}>
                <MemoBox {...props} className={classNames(className, themeClassName)} ref={ref}>
                    {children}
                </MemoBox>
            </ThemeContext.Provider>
        )
    }
)

export const ThemeProvider = React.memo(ThemeProviderInner)
